// src/components/roles/RolesTable.jsx
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material';
import toast from 'react-hot-toast';

import { useGetRoles } from '../../hooks/roles/useGetRoles';
import { useDeleteRoles } from '../../hooks/roles/useDeleteRoles';
import { usePermissions } from '../../auth/PermissionsContext';
import useTableActions from '../../hooks/common/useTableActions';

import ModelShowRole from './ModelShowRole';
import ResourceTableLayout from '../global/Table/TableLayout';
import RolesTableRow from './Table/RolesTableRow';
import RolesTableColumns from './Table/RolesTableColumns';

const RolesTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || 'en';
    const isArabic = lng === 'ar';
    const { has, userRoles, userRolesNames } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has('roles_list'),
            create: has('roles_create'),
            edit: has('roles_edit'),
            delete: has('roles_delete'),
            view: has('roles_show'),
        }),
        [has]
    );

    const navigate = useNavigate();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const { deleteRoles, isDelete } = useDeleteRoles();

    // ✅ استخدام useTableActions للإجراءات المشتركة
    const {
        page,
        perPage,
        search,
        sortBy,
        sortDir,
        menuAnchorEl,
        selectedItem,
        setSearch,
        handleRequestSort,
        handleMenuOpen,
        handleMenuClose,
        handlePageChange,
        handlePerPageChange,
    } = useTableActions({
        basePath: '/roles',
        deleteSingle: deleteRoles,
        isArchive: false,
    });

    const [openView, setOpenView] = useState(false);
    const [viewRoleId, setViewRoleId] = useState(null);

    const filters = useMemo(
        () => ({
            search: search || undefined,
            sort_by: sortBy,
            sort_dir: sortDir,
        }),
        [search, sortBy, sortDir]
    );

    const { isLoading, error, roles, meta } = useGetRoles(page, perPage, filters);

    // ✅ التحقق من أن الدور المحدد هو أحد أدوار المستخدم الحالي
    const isUserOwnRole = useMemo(() => {
        if (!selectedItem) return false;
        const hasRoleById = userRoles.some(role => role.id === selectedItem.id);
        const hasRoleByName = userRolesNames.includes(selectedItem.name);
        return hasRoleById || hasRoleByName;
    }, [selectedItem, userRoles, userRolesNames]);

    // ✅ معالج العرض
    const handleView = () => {
        if (!perms.view) {
            toast.error(isArabic ? 'لا تملك صلاحية العرض' : 'No view permission');
            handleMenuClose();
            return;
        }
        if (selectedItem) {
            setViewRoleId(selectedItem.id);
            setOpenView(true);
        }
        handleMenuClose();
    };

    // ✅ معالج التعديل
    const handleEdit = () => {
        if (!perms.edit) {
            toast.error(isArabic ? 'لا تملك صلاحية التعديل' : 'No edit permission');
            handleMenuClose();
            return;
        }
        if (isUserOwnRole && !userRolesNames.includes('super_admin')) {
            toast.error(
                isArabic
                    ? 'لا يمكنك تعديل الدور الخاص بك'
                    : 'You cannot edit your own role'
            );
            handleMenuClose();
            return;
        }
        if (selectedItem) {
            navigate(`/roles/${selectedItem.id}/edit`);
        }
        handleMenuClose();
    };

    // ✅ معالج الحذف
    const handleDelete = () => {
        if (!perms.delete) {
            toast.error(isArabic ? 'لا تملك صلاحية الحذف' : 'No delete permission');
            handleMenuClose();
            return;
        }
        if (isUserOwnRole) {
            toast.error(
                isArabic
                    ? 'لا يمكنك حذف الدور الخاص بك'
                    : 'You cannot delete your own role'
            );
            handleMenuClose();
            return;
        }
        if (selectedItem) {
            deleteRoles(selectedItem.id);
        }
        handleMenuClose();
    };

    // ✅ معالج زر الإضافة
    const handleAddClick = () => {
        if (!perms.create) {
            toast.error(isArabic ? 'لا تملك صلاحية الإنشاء' : 'No create permission');
            return;
        }
        navigate('/roles/create');
    };

    // ✅ التحقق من أن الدور ينتمي للمستخدم
    const checkIfUserRole = (roleItem) => {
        const hasById = userRoles.some(role => role.id === roleItem.id);
        const hasByName = userRolesNames.includes(roleItem.name);
        return hasById || hasByName;
    };

    const renderRow = (params) => (
        <RolesTableRow
            key={params.key || params.item.id}
            role={params.item}
            index={params.index}
            isArabic={params.isArabic}
            isDark={isDark}
            page={params.page}
            perPage={params.perPage}
            order={sortDir}
            totalItems={roles?.length || 0}
            onMenuOpen={(event) => handleMenuOpen(event, params.item)}
            menuDisabled={!perms.edit && !perms.delete && !perms.view}
            isUserOwnRole={checkIfUserRole(params.item)}
        />
    );

    return (
        <>
            <ResourceTableLayout
                title={isArabic ? 'الأدوار' : 'Roles'}
                subtitle={
                    isArabic
                        ? 'إدارة الأدوار والصلاحيات في النظام'
                        : 'Manage system roles and permissions'
                }
                search={search}
                onSearchChange={setSearch}
                addButtonLabel={isArabic ? 'إنشاء دور جديد' : 'Create new role'}
                onAddClick={handleAddClick}
                addButtonDisabled={!perms.create}
                addButtonTooltip={
                    !perms.create
                        ? isArabic
                            ? 'لا تملك صلاحية الإنشاء'
                            : 'No create permission'
                        : undefined
                }
                isLoading={isLoading}
                error={error}
                items={roles}
                meta={meta}
                page={page}
                perPage={perPage}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                sortBy={sortBy}
                sortDir={sortDir}
                onRequestSort={handleRequestSort}
                columns={RolesTableColumns}
                renderRow={renderRow}
                menuAnchorEl={menuAnchorEl}
                openMenu={Boolean(menuAnchorEl)}
                onMenuOpen={(event) => handleMenuOpen(event, {})}
                onMenuClose={handleMenuClose}
                onMenuView={perms.view ? handleView : undefined}
                onMenuEdit={perms.edit ? handleEdit : undefined}
                onMenuDelete={perms.delete ? handleDelete : undefined}
                menuDisableView={!perms.view}
                menuDisableEdit={!perms.edit || (isUserOwnRole && !userRolesNames.includes('super_admin'))}
                menuDisableDelete={!perms.delete}
                isDeletingSingle={isDelete}
                emptyLabelAr="لا توجد أدوار"
                emptyLabelEn="No roles"
                loadErrorAr="حدث خطأ أثناء تحميل الأدوار"
                loadErrorEn="Failed to load roles"
            />

            {openView && (
                <ModelShowRole
                    roleId={viewRoleId}
                    open={openView}
                    onClose={() => setOpenView(false)}
                />
            )}
        </>
    );
};

export default RolesTable;
